/*
 * Provides `React.useId` on React 16.
 *
 * `@amzn/core-grc-global-nav-frontend` declares `react: ^16.8.0 || ^17 || ^18 || ^19`
 * as a peer, but `JupiterSideNav` calls `useId` unconditionally. `useId` only exists
 * from React 18, so on this app's React 16.14 the rail throws
 * "(0, react.useId) is not a function" on first render.
 *
 * The rail uses the value purely as a unique string to wire `aria-controls` /
 * `aria-labelledby` between a group row and its child list, so a per-instance unique
 * id is a complete substitute. Cloudscape's own `@amzn/awsui-component-toolkit`
 * takes exactly this approach in `internal/use-unique-id` (`React.useId ?? random`);
 * the global-nav package simply omits the fallback.
 *
 * Import this module before rendering anything from the global-nav package. It is a
 * no-op once the app is on React 18, so it can be deleted at that point.
 *
 * Server-side rendering caveat: React's own `useId` is hydration-stable, and a
 * counter is not. This app is client-rendered only, so that difference cannot bite
 * here — but it is the reason this must not outlive the React 16 dependency.
 */
import { useRef } from 'react';

/**
 * Hands out process-unique id suffixes. Wrapped in a class so the counter is not a
 * bare mutable module-level variable.
 */
class UseIdCounter {
  private count = 0;

  /**
   * @returns {string} A value that has not been returned before in this document.
   */
  next(): string {
    this.count += 1;
    return `r${this.count.toString(36)}`;
  }
}

const counter = new UseIdCounter();

/**
 * Stand-in for React 18's `useId`.
 *
 * Must obey the hook rules like the real thing: it is called unconditionally and
 * stores its value in a ref, so the id is allocated once per component instance and
 * survives every re-render.
 * @returns {string} A stable, unique id for the calling component instance.
 */
function useIdFallback(): string {
  const idRef = useRef<string | null>(null);
  if (idRef.current === null) idRef.current = counter.next();
  return idRef.current;
}

/*
 * The patch has to land on React's real CommonJS `module.exports`, which is why this
 * uses `require` rather than the imported binding.
 *
 * React ships as CommonJS with no `__esModule` marker, so `import * as React` is
 * compiled to `_interopRequireWildcard(require('react'))` — a fresh object with the
 * exports copied onto it. Assigning to that copy is invisible to anyone else importing
 * React. `require('react')` returns the one shared exports object instead.
 *
 * Consumers read the hook as a live property (`(0, _react.useId)(...)` after Babel,
 * `react__WEBPACK_IMPORTED_MODULE__.useId` after webpack), so defining it here is
 * picked up even by modules that imported React before this ran.
 *
 * Note this makes `useId` visible process-wide, not just to the global nav — that is
 * intentional (the nav reads it off the same shared object), but it also means any
 * code that feature-detects `React.useId` (e.g. Cloudscape's `useUniqueId`) will
 * switch to this counter once the nav ships. Nothing imports this module yet, so it is
 * dormant; when the nav lands, re-check component snapshots for churned ids.
 */
// eslint-disable-next-line @typescript-eslint/no-var-requires
const reactExports: { useId?: () => string } = require('react');

if (typeof reactExports.useId !== 'function') {
  reactExports.useId = useIdFallback;
}

export default useIdFallback;
